import { useState, useEffect } from 'react';
import {
  Input,
  RadioGroup,
  Checkbox,
  SpaceBetween,
  Button,
  Box,
  Popover,
  TextContent,
} from '@cloudscape-design/components';
import { ResponseType } from 'src/types/helios/Question';
import EmployeePicker from 'src/components/common/EmployeePicker';
import CompositionSafeTextarea from 'src/components/CompositionSafeTextarea';

export interface DataCollectionInputProps {
  /** The response type that determines which control to render. */
  'responseType': ResponseType;
  /** Current value (text, number string, selected option, comma-separated for multi, alias). */
  'value': string;
  /** Called when value changes. Parent manages state/save. */
  'onChange': (value: string) => void;
  /** Options for SINGLE_SELECT/MULTI_SELECT/YES_NO types. */
  'options'?: string[];
  /** Whether the control is read-only (audit completed/expired). */
  'readOnly'?: boolean;
  /** Program ID for EmployeePicker (USER_ALIAS type). */
  'programId'?: string;
  /** Test ID prefix for the control. */
  'data-testid'?: string;
}

/** Maps responseType to a user-facing label shown above the control. */
const TYPE_LABELS: Record<string, string> = {
  [ResponseType.TEXT]: 'Text response',
  [ResponseType.NUMERIC]: 'Numeric response',
  [ResponseType.SINGLE_SELECT]: 'Select one option',
  [ResponseType.MULTI_SELECT]: 'Select all that apply',
  [ResponseType.YES_NO]: 'Yes or No',
  [ResponseType.USER_ALIAS]: 'Employee alias',
};

/** Maps responseType to helper/constraint text shown below the control. */
const TYPE_INFO: Record<string, string> = {
  [ResponseType.TEXT]: 'Enter a text response (max 100 characters).',
  [ResponseType.NUMERIC]: 'Enter a numeric value. Decimals are allowed.',
  [ResponseType.SINGLE_SELECT]: 'Choose one option from the list.',
  [ResponseType.MULTI_SELECT]: 'Check all options that apply, then click Apply.',
  [ResponseType.YES_NO]: 'Select Yes or No.',
  [ResponseType.USER_ALIAS]: 'Search and select an employee by alias or name.',
};

/**
 * Renders the appropriate input control for a data-collection sub-question
 * based on its responseType. Includes a "Response" header, type-specific label
 * with Info popover, the control, and helper text.
 *
 * Gating: This component is rendered only when enableEnhancedResponseTypes is true
 * and the sub-question's responseType is a data-collection type. The parent
 * component (PerformAuditQuestionView) handles that gate.
 */
const DataCollectionInput = ({
  responseType,
  value,
  onChange,
  options = [],
  readOnly = false,
  programId,
  'data-testid': dataTestId,
}: DataCollectionInputProps) => {
  const typeLabel = TYPE_LABELS[responseType] || 'Response';
  const typeInfo = TYPE_INFO[responseType] || '';

  return (
    <SpaceBetween data-testid={dataTestId} size="xs">
      {/* Header */}
      <TextContent>
        <h4>Response</h4>
      </TextContent>

      {/* Type label + Info popover */}
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        <TextContent>
          <strong>{typeLabel}</strong>
        </TextContent>
        <Popover header="Info" content={typeInfo} triggerType="text" size="small">
          <Box color="text-status-info" fontSize="body-s" fontWeight="bold">
            Info
          </Box>
        </Popover>
      </SpaceBetween>

      {/* Input control */}
      {renderControl(
        responseType,
        value,
        onChange,
        options,
        readOnly,
        programId,
        dataTestId,
      )}

      {/* Helper text */}
      <HelperText responseType={responseType} value={value} />
    </SpaceBetween>
  );
};

/** Renders the actual input control based on responseType. */
const renderControl = (
  responseType: ResponseType,
  value: string,
  onChange: (value: string) => void,
  options: string[],
  readOnly: boolean,
  programId?: string,
  dataTestId?: string,
) => {
  switch (responseType) {
    case ResponseType.TEXT:
      return (
        <CompositionSafeTextarea
          data-testid={dataTestId ? `${dataTestId}-textarea` : undefined}
          value={value}
          onChange={({ detail }) => {
            if (detail.value.length <= 100) {
              onChange(detail.value);
            }
          }}
          placeholder="Type your response"
          readOnly={readOnly}
          rows={3}
        />
      );

    case ResponseType.NUMERIC:
      return (
        <Input
          data-testid={dataTestId ? `${dataTestId}-input` : undefined}
          value={value}
          onChange={({ detail }) => {
            // Allow empty, digits, decimal point, and leading minus
            if (detail.value === '' || /^-?\d*\.?\d*$/.test(detail.value)) {
              onChange(detail.value);
            }
          }}
          placeholder="0"
          readOnly={readOnly}
          inputMode="decimal"
        />
      );

    case ResponseType.SINGLE_SELECT:
      return (
        <RadioGroup
          data-testid={dataTestId ? `${dataTestId}-radio` : undefined}
          value={value || null}
          onChange={({ detail }) => onChange(detail.value)}
          items={options.map((opt) => ({ value: opt, label: opt }))}
          readOnly={readOnly}
        />
      );

    case ResponseType.MULTI_SELECT:
      return (
        <MultiSelectCheckboxGroup
          dataTestId={dataTestId ? `${dataTestId}-multi` : undefined}
          value={value}
          onChange={onChange}
          options={options}
          readOnly={readOnly}
        />
      );

    case ResponseType.YES_NO:
      return (
        <RadioGroup
          data-testid={dataTestId ? `${dataTestId}-radio` : undefined}
          value={value || null}
          onChange={({ detail }) => onChange(detail.value)}
          items={options.map((opt) => ({ value: opt, label: opt }))}
          readOnly={readOnly}
        />
      );

    case ResponseType.USER_ALIAS:
      return (
        <EmployeePicker
          data-testid={dataTestId ? `${dataTestId}-picker` : undefined}
          programId={programId || ''}
          value={value}
          onSelect={(login) => onChange(login)}
          onClear={() => onChange('')}
          disabled={readOnly}
        />
      );

    default:
      return null;
  }
};

/** Helper text shown below the control — character count, constraint info, selection count. */
const HelperText = ({
  responseType,
  value,
}: {
  responseType: ResponseType;
  value: string;
}) => {
  switch (responseType) {
    case ResponseType.TEXT:
      return (
        <Box color="text-body-secondary" fontSize="body-s">
          {value.length}/100 characters
        </Box>
      );
    case ResponseType.NUMERIC:
      return (
        <Box color="text-body-secondary" fontSize="body-s">
          Numbers only. Decimals allowed.
        </Box>
      );
    case ResponseType.MULTI_SELECT: {
      const count = value ? value.split(',').length : 0;
      return (
        <Box color="text-body-secondary" fontSize="body-s">
          {count} selected
        </Box>
      );
    }
    default:
      return null;
  }
};

/**
 * Internal component for MULTI_SELECT. Renders checkboxes with an "Apply" button.
 * Local checkbox state is held internally until user explicitly clicks Apply,
 * preventing rapid-fire API calls from individual checkbox toggles.
 */
const MultiSelectCheckboxGroup = ({
  dataTestId,
  value,
  onChange,
  options,
  readOnly,
}: {
  dataTestId?: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  readOnly: boolean;
}) => {
  // Local draft state — only committed to parent on Apply
  const committedValues = value ? value.split(',') : [];
  const [draftValues, setDraftValues] = useState<string[]>(committedValues);
  const [isDirty, setIsDirty] = useState(false);

  // Sync draft from parent when value changes externally (e.g., page load, Redux sync)
  useEffect(() => {
    const newCommitted = value ? value.split(',') : [];
    setDraftValues(newCommitted);
    setIsDirty(false);
  }, [value]);

  const handleToggle = (opt: string, checked: boolean) => {
    const updated = checked
      ? [...draftValues, opt]
      : draftValues.filter((v) => v !== opt);
    setDraftValues(updated);
    setIsDirty(true);
  };

  const handleApply = () => {
    onChange(draftValues.join(','));
    setIsDirty(false);
  };

  return (
    <SpaceBetween data-testid={dataTestId} size="xs">
      {options.map((opt) => (
        <Checkbox
          key={opt}
          checked={draftValues.includes(opt)}
          onChange={({ detail }) => handleToggle(opt, detail.checked)}
          disabled={readOnly}
        >
          {opt}
        </Checkbox>
      ))}
      {!readOnly && (
        <Button
          data-testid={dataTestId ? `${dataTestId}-apply` : undefined}
          variant="normal"
          disabled={!isDirty}
          onClick={handleApply}
        >
          Apply selection
        </Button>
      )}
    </SpaceBetween>
  );
};

export default DataCollectionInput;
